// Hjälpfunktioner för konsistent namnhantering genom hela frontend

/**
 * Hämtar fullständigt namn från customerInfo eller liknande objekt
 * @param {Object} customerInfo - Objekt med namn-information
 * @returns {string} - Fullständigt namn
 */
export const getFullName = (customerInfo) => {
  if (!customerInfo) return 'Okänt namn';
  
  // Använd nya separata fält om de finns
  if (customerInfo.firstName || customerInfo.lastName) {
    return `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim();
  }
  
  // Fallback till gammalt kombinerat namn
  if (customerInfo.name) {
    return customerInfo.name.trim();
  }
  
  return 'Okänt namn';
};

/**
 * Hämtar namnet på AVTALSPARTEN — den som offerten ställs till och som står på
 * avtalet.
 *
 * För en privatperson är det personens namn, precis som getFullName ger. För en
 * bostadsrättsförening är det föreningens namn, medan firstName/lastName på
 * kontot bär representantens namn.
 *
 * Använd den här där den juridiska motparten menas: offert, avtal och
 * SharePoint-mappen. Använd getFullName där en PERSON tilltalas — hälsningar,
 * mötesdeltagare, mailmottagare. Man hälsar på Anna, inte på föreningen.
 *
 * @param {Object} kund - Kundobjekt eller customerInfo
 * @returns {string} - Avtalspartens namn
 */
export const hamtaBestallarnamn = (kund) => {
  if (!kund) return 'Okänt namn';

  if (kund.kundtyp === 'brf' && kund.foreningsnamn) {
    return kund.foreningsnamn.trim();
  }

  return getFullName(kund);
};

/**
 * Är kunden en bostadsrättsförening? Styr ROT, avtalsform och etiketter.
 * @param {Object} kund - Kundobjekt eller customerInfo
 * @returns {boolean}
 */
export const arBrfKund = (kund) => kund?.kundtyp === 'brf';

/**
 * Alla namn en kund kan sökas på, hopslaget till en sträng.
 *
 * Vyerna visar avtalsparten, men den som söker skriver lika gärna
 * kontaktpersonens namn — man minns "Carl" utan att minnas vilken förening han
 * företräder. Filtrera därför aldrig på enbart hamtaBestallarnamn: då blir
 * kontaktpersonerna osökbara.
 *
 * @param {Object} kund - Kundobjekt eller customerInfo
 * @returns {string} - Gemener, för direkt jämförelse med söktermen
 */
export const hamtaSokbartKundnamn = (kund) => {
  if (!kund) return '';

  const person = `${kund.firstName || ''} ${kund.lastName || ''}`.trim();

  return [kund.foreningsnamn, person, kund.name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

/**
 * Hämtar separata namn-delar från customerInfo
 * @param {Object} customerInfo - Objekt med namn-information
 * @returns {Object} - { firstName, lastName }
 */
export const getNameParts = (customerInfo) => {
  if (!customerInfo) return { firstName: '', lastName: '' };
  
  // Använd nya separata fält om de finns
  if (customerInfo.firstName !== undefined || customerInfo.lastName !== undefined) {
    return {
      firstName: customerInfo.firstName || '',
      lastName: customerInfo.lastName || ''
    };
  }
  
  // Fallback: dela upp gammalt kombinerat namn
  if (customerInfo.name) {
    const nameParts = customerInfo.name.trim().split(/\s+/);
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || ''
    };
  }
  
  return { firstName: '', lastName: '' };
};

/**
 * Formaterar adress från projektInfo eller customerInfo
 * @param {Object} projectInfo - Projektinformation
 * @param {Object} customerInfo - Kundinformation (fallback)
 * @returns {Object} - { address, postCode, city, formattedAddress }
 */
export const getAddressInfo = (projectInfo, customerInfo = null) => {
  let address = '', postCode = '', city = '';
  
  // Prioritera projectInfo för adress
  if (projectInfo) {
    // Använd separata fält om de finns
    if (projectInfo.address !== undefined || projectInfo.postCode !== undefined || projectInfo.city !== undefined) {
      address = projectInfo.address || '';
      postCode = projectInfo.postCode || '';
      city = projectInfo.city || '';
    } else if (projectInfo.address && typeof projectInfo.address === 'string') {
      // Fallback: försök parsa från kombinerat adressfält
      address = projectInfo.address;
    }
  }
  
  // Fallback till customerInfo om inget hittades i projectInfo
  if (!address && !postCode && !city && customerInfo?.address) {
    address = customerInfo.address;
  }
  
  // Skapa formaterad adress för visning
  const formattedAddress = (() => {
    if (!address && !postCode && !city) return 'Ej angivet';
    
    const line1 = address?.trim() || '';
    const line2 = `${String(postCode).trim()} ${String(city).trim()}`.trim();
    
    if (line1 && line2) return `${line1}, ${line2}`;
    if (line1) return line1;
    if (line2) return line2;
    return 'Ej angivet';
  })();
  
  return {
    address,
    postCode,
    city,
    formattedAddress
  };
};

/**
 * Kontrollerar om ett objekt har tillräcklig namn-information
 * @param {Object} customerInfo - Objekt med namn-information
 * @returns {boolean} - True om namn finns
 */
export const hasValidName = (customerInfo) => {
  if (!customerInfo) return false;
  
  // Kolla nya separata fält
  if (customerInfo.firstName || customerInfo.lastName) return true;
  
  // Kolla gammalt kombinerat namn
  return !!(customerInfo.name && customerInfo.name.trim());
};

/**
 * Skapar en display-vänlig version av kundinfo för listor
 * @param {Object} customerInfo - Kundinformation
 * @param {boolean} includeEmail - Om email ska inkluderas
 * @returns {string} - Formaterad sträng för visning
 */
export const getCustomerDisplayInfo = (customerInfo, includeEmail = false) => {
  if (!customerInfo) return 'Okänd kund';
  
  const name = getFullName(customerInfo);
  if (!includeEmail || !customerInfo.email) return name;
  
  return `${name} (${customerInfo.email})`;
};
